// Tema claro / oscuro / según el sistema (ARCHITECTURE §9, UX §2.6). Se aplica al instante en <html>,
// se guarda en `localStorage['atte.theme']` (para no parpadear al cargar, /theme-init.js) y en el perfil
// (`PATCH /me/preferences`), que manda entre dispositivos.
import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { SessionResponse } from '@atte/contracts';
import { getThemePreference, setThemePreference, useTheme as useThemeBase, THEME_STORAGE_KEY, type ThemePreference } from '@/components/ui/theme';
import { api } from './api';
import { qk } from './queryKeys';

export { THEME_STORAGE_KEY, getThemePreference, setThemePreference };
export type { ThemePreference };

export const THEME_LABELS: Record<ThemePreference, string> = {
  light: 'Claro',
  dark: 'Oscuro',
  system: 'Según el sistema',
};

/** Guarda el tema en el perfil (último gana, sin versión). Errores silenciosos: es una preferencia. */
export async function saveThemeToProfile(theme: ThemePreference): Promise<void> {
  try {
    await api.patch('/me/preferences', { theme });
  } catch {
    /* sin conexión: queda en localStorage y se reintenta la próxima vez que lo cambie */
  }
}

/**
 * Tema con persistencia en el perfil. `setTheme` aplica, guarda local y en el servidor, y actualiza la sesión
 * en caché para que no vuelva al valor anterior.
 */
export function useThemePreference(): {
  theme: ThemePreference;
  resolved: 'light' | 'dark';
  setTheme: (t: ThemePreference) => void;
} {
  const { preference, resolved } = useThemeBase();
  const qc = useQueryClient();
  const setTheme = useCallback(
    (theme: ThemePreference) => {
      setThemePreference(theme);
      qc.setQueryData<SessionResponse>(qk.session, (s) => (s ? { ...s, user: { ...s.user, theme } } : s));
      void saveThemeToProfile(theme);
    },
    [qc],
  );
  return { theme: preference, resolved, setTheme };
}

/**
 * Al cargar la sesión, el tema del perfil manda sobre el del navegador (se cambió en otro dispositivo).
 * Solo se aplica una vez por usuario para no pisar un cambio recién hecho.
 */
export function useSyncThemeFromProfile(session: SessionResponse | undefined): void {
  const applied = useRef<string | null>(null);
  useEffect(() => {
    if (!session) return;
    const key = session.user.id;
    if (applied.current === key) return;
    applied.current = key;
    if (session.user.theme !== getThemePreference()) setThemePreference(session.user.theme);
  }, [session]);
}
